/**
 * Button Component
 * Reusable button with multiple variants following the shared design system
 */
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'icon' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-button',
  standalone: true,
  imports: [CommonModule],
  styles: [`
    :host {
      display: inline-flex;
      vertical-align: middle;
    }
  `],
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [class]="getButtonClasses()"
      (click)="handleClick($event)">
      <svg *ngIf="loading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <ng-content></ng-content>
    </button>
  `
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() fullWidth = false;
  @Output() buttonClick = new EventEmitter<MouseEvent>();

  handleClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.buttonClick.emit(event);
    }
  }

  getButtonClasses(): string {
    const variantMap: Record<ButtonVariant, string> = {
      primary: 'pe-btn-primary',
      secondary: 'pe-btn-secondary',
      ghost: 'pe-btn-ghost',
      icon: 'pe-btn-ghost pe-btn-icon',
      danger: 'pe-btn-danger',
      success: 'pe-btn-success'
    };
    const sizeMap: Record<ButtonSize, string> = {
      sm: 'pe-btn-sm',
      md: 'pe-btn-md',
      lg: 'pe-btn-lg'
    };
    const width = this.fullWidth ? 'pe-btn-block' : '';
    return `pe-btn ${variantMap[this.variant]} ${sizeMap[this.size]} ${width}`.trim();
  }
}
